import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import { deleteSprTemplateNazn, fetchSprTemplateNaznList } from "./spr-template-nazn-api";
import SprTemplateNaznDrawer from "./spr-template-nazn-drawer";
import { getLawFlag, normalizeZag } from "./spr-template-nazn-utils";
import "./spr-template-nazn.css";

/**
 * Справочник шаблонов назначений — список названий + drawer-карточка.
 */
const SprTemplateNazn = () => {
  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 10) === 1;

  const [templates, setTemplates] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerIsNew, setDrawerIsNew] = useState(false);

  const listReqRef = useRef(0);

  const loadList = useCallback(() => {
    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    return fetchSprTemplateNaznList()
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const rows = Array.isArray(data)
          ? data.map(normalizeZag).filter((r) => r.idShb != null)
          : [];
        setTemplates(rows);
        setSelectedId((prev) => {
          if (prev != null && rows.some((r) => r.idShb === prev)) return prev;
          return null;
        });
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить шаблоны");
        setTemplates([]);
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filteredTemplates = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [templates, nameFilter]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.idShb === selectedId) || null,
    [templates, selectedId]
  );

  const openCreate = () => {
    if (!canEdit) return;
    setDrawerIsNew(true);
    setDrawerId(null);
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    if (row?.idShb == null) return;
    setSelectedId(row.idShb);
    setDrawerIsNew(false);
    setDrawerId(row.idShb);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const onDrawerSaved = (saved) => {
    const id = toNumber(pick(saved, "idShb", "IdShb"));
    loadList().then(() => {
      if (id != null) {
        setSelectedId(id);
        setDrawerId(id);
        setDrawerIsNew(false);
      }
    });
  };

  const onZagDel = () => {
    if (!selectedTemplate || !canEdit) return;
    Modal.confirm({
      title: "Удалить?",
      content: `Шаблон «${selectedTemplate.name || selectedTemplate.idShb}» будет удалён.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprTemplateNazn(selectedTemplate.idShb)
          .then(() => {
            message.success("Шаблон удалён");
            if (drawerId === selectedTemplate.idShb) closeDrawer();
            setSelectedId(null);
            return loadList();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "idShb",
      key: "idShb",
      width: 72,
      align: "right"
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      render: (name, row) => name || `#${row.idShb}`
    }
  ];

  return (
    <div className="spr-template-nazn">
      {error ? (
        <Alert className="spr-template-nazn__error" type="error" showIcon message={error} />
      ) : null}

      <div className="spr-template-nazn__body">
        <div className="spr-template-nazn__list">
          <div className="spr-template-nazn__toolbar">
            <div className="spr-template-nazn__extra-actions">
              <Tooltip title="Добавить">
                <span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!canEdit}
                    aria-label="Добавить"
                    onClick={openCreate}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Изменить">
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedTemplate}
                    aria-label="Изменить"
                    onClick={() => openEdit(selectedTemplate)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Удалить">
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!canEdit || !selectedTemplate}
                    aria-label="Удалить"
                    onClick={onZagDel}
                  />
                </span>
              </Tooltip>
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-template-nazn__filter"
              placeholder="Фильтр по наименованию"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-template-nazn__list-body">
            {loadingList && !templates.length ? (
              <div className="spr-template-nazn__center">
                <Spin />
              </div>
            ) : (
              <Table
                className="spr-template-nazn__table"
                size="small"
                rowKey="idShb"
                pagination={false}
                columns={columns}
                dataSource={filteredTemplates}
                loading={loadingList}
                rowClassName={(row) =>
                  row.idShb === selectedId ? "spr-template-nazn__row--selected" : ""
                }
                onRow={(row) => ({
                  onClick: () => {
                    setSelectedId(row.idShb);
                    openEdit(row);
                  }
                })}
                locale={{
                  emptyText: templates.length ? "Ничего не найдено" : "Нет шаблонов"
                }}
              />
            )}
          </div>
        </div>
      </div>

      <SprTemplateNaznDrawer
        open={drawerOpen}
        idShb={drawerId}
        isNew={drawerIsNew}
        canEdit={canEdit}
        onClose={closeDrawer}
        onSaved={onDrawerSaved}
      />
    </div>
  );
};

export default SprTemplateNazn;
